import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import useOfferList from 'hooks/Offers/useOfferList'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import type { OfferListEventsDispatchAction } from 'components/OfferList/OfferListEventsContext'
import { OfferListEventsProvider, OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { without } from 'lib/array/arrayUtils'
import { trackClientEvent } from 'analytics/analytics'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIntl } from 'react-intl'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { queryKeyDestinationId, queryKeyDestinationName, queryKeyAdults, queryKeyChildren, queryKeyCheckIn, queryKeyCheckOut } from 'constants/url'
import { decodeOfferPageParams } from 'lib/offer/offerPageUtil'
import { ANYWHERE_PLACE_ID, ANYWHERE_NAME } from 'constants/search'

const CarouselContainer = styled.div`
  padding: 0 ${rem(24)} ${rem(24)};
`

const ViewAllCard = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(20)};
  width: ${rem(320)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)};
  }

  ${mediaQueryUp.largeDesktop} {
    padding: ${rem(60)};
  }
`

interface Props {
  offerId: string
  propertyName?: string
}

const RECOMMENDATION_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  'bedbank_hotel',
]

const MIN_OFFERS_TO_DISPLAY = 2

function ExitIntentModal(props: Props) {
  const { offerId } = props
  const modalElement = useModalElementContext()

  const intl = useIntl()
  const isMobileDevice = useIsMobileDevice()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const relatedTravelItems = useAppSelector(state => state.offer.relatedTravelItems[offerId])
  const routerSearch = useAppSelector(state => state.router.location.search)

  // Build View All URL with destination and dates from URL params
  const viewAllUrl = useMemo(() => {
    // Find the first breadcrumb item with a placeId (destination)
    const destinationItem = relatedTravelItems?.find(item => item.placeId)

    // Get dates from current URL params
    const urlParams = decodeOfferPageParams(routerSearch)

    const searchParams = new URLSearchParams()

    // Add dates if available from URL, otherwise it will be anytime search
    if (urlParams.dates.checkIn) {
      searchParams.set(queryKeyCheckIn, urlParams.dates.checkIn)
    }
    if (urlParams.dates.checkOut) {
      searchParams.set(queryKeyCheckOut, urlParams.dates.checkOut)
    }

    // Use destination from breadcrumbs or fallback to Anywhere
    if (destinationItem?.placeId) {
      searchParams.set(queryKeyDestinationId, destinationItem.placeId)
      searchParams.set(queryKeyDestinationName, destinationItem.text)
    } else {
      searchParams.set(queryKeyDestinationId, ANYWHERE_PLACE_ID)
      searchParams.set(queryKeyDestinationName, ANYWHERE_NAME)
    }

    // Required params for search page
    searchParams.set(queryKeyAdults, String(config.search.defaultOccupants.adults))
    searchParams.set(queryKeyChildren, 'none')

    return `/search?${searchParams.toString()}`
  }, [relatedTravelItems, routerSearch])

  const handleClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  // Fetch similar offers using graph recommendations
  const filters: App.OfferListFilters = useMemo(() => ({
    lere: 'graph',
    region: regionCode,
    baseOfferId: offerId,
    offerTypes: RECOMMENDATION_TYPES,
  }), [offerId, regionCode])

  const offerList = useOfferList(filters, {
    excludedIds: [offerId],
  })

  // Create offer list for carousel (excluding current offer)
  const carouselOfferList: App.OfferList = useMemo(() => ({
    ...offerList,
    offerIds: without(offerList.offerIds, offerId),
  }), [offerList, offerId])

  const tracking = useOfferListTracking('ExitIntentModal', {
    ga: {
      category: 'ExitIntentModal',
      label: 'graph-recommendations',
    },
    key: 'exit-intent-modal',
  })

  const handleListEvent = useCallback((dispatchAction: OfferListEventsDispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      trackClientEvent({
        subject: 'exit-intent-offer-click',
        action: `position: ${dispatchAction.position + 1}`,
        category: 'exit-intent-modal',
        type: 'interaction',
        optimizelyEventId: 'exit_intent_modal_click',
        optimizelyEventKey: 'exit_intent_modal_offer_click',
      })
    }
  }, [])

  const modalTitle = isMobileDevice
    ? intl.formatMessage({
        id: 'MlbxLkX_tk6zFZBTLLaN',
        defaultMessage: 'Explore similar places',
        description: 'Heading title for exit intent modal on mobile on the offer detail page',
      })
    : intl.formatMessage({
        id: 'GuS1JsZMqSfK1vRsVH8Y',
        defaultMessage: 'Before you go, explore similar places',
        description: 'Heading title for exit intent modal on desktop on the offer detail page',
      })

  const lookingForSomethingElseText = intl.formatMessage({
    id: 'CseEGxU2mqJAIq5V-7nt',
    defaultMessage: 'Looking for something else?',
    description: 'Exit intent modal view all card heading on the offer detail page',
  })

  const viewAllText = intl.formatMessage({
    id: 'mKwRYhiQnpDFx3AZq-wh',
    defaultMessage: 'View all',
    description: 'Exit intent modal view all button on the offer detail page',
  })

  const hasEnoughOffers = carouselOfferList.offerIds.length >= MIN_OFFERS_TO_DISPLAY

  // Don't render if not enough offers
  if (!offerList.fetching && !hasEnoughOffers) {
    return null
  }

  return (
    <ModalBase size="XL">
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <CarouselContainer>
          <OfferListEventsProvider tracking={tracking} onListEvent={handleListEvent}>
            <OfferListCarousel
              overrideOfferList={carouselOfferList}
              tabletLimit="default"
              tileStyle="card-default"
              tileSize="small"
              size="block"
            >
              <ViewAllCard direction="vertical" horizontalAlign="center" verticalAlign="center" gap={24}>
                <VerticalSpacer gap={8}>
                  <Heading align="center" variant="heading5">{lookingForSomethingElseText}</Heading>
                </VerticalSpacer>
                <TextButton kind="primary" size="medium" shape="square" to={viewAllUrl} onClick={handleClose}>
                  {viewAllText}
                </TextButton>
              </ViewAllCard>
            </OfferListCarousel>
          </OfferListEventsProvider>
        </CarouselContainer>
      </ModalBody>
    </ModalBase>
  )
}

export default ExitIntentModal
